import React, { useEffect } from "react"
import { useNavigate } from "react-router-dom"
import { EmptyList, FlexColumn, FlexColumnOverflow, SmallInfo } from "../Order/OrderTable/OrderTable.styled"
import TableRow from "../Ui/Table/TableRow"
import TableBlurLoading from "../Ui/Table/TableBlurLoading"
import ErrorMessage from "../Ui/Messages/ErrorMessage"
import { ReactComponent as CartIcon } from "../../images/icons/cart-shopping.svg"
import { Container, StyledTableHeader, TruncatedText } from "./TopDiscountCodes.styled"
import { topDiscountData, topDiscountRulesData } from "../../views/Dashboards/DashboardSalesAggregations"
import Dinero from "dinero.js"
import FlagIcon from "../Ui/FlagIcon"
import { Tooltip } from "../Ui/Tooltip"
import { useDispatch } from "react-redux"
import { setTopDiscountCodesExportData } from "lib/store/services/analytics/slice"

type Props = {
  data: topDiscountData[] | topDiscountRulesData[]
  loading: boolean
  error: object | undefined
  type: 'codes' | 'code rules' | 'external rules'
}

export const TopDiscountCodes = ({ data, loading, error, type }: Props) => {
  const navigate = useNavigate()
  const dispatch = useDispatch()

  // Dispatch export data to Redux when data changes
  useEffect(() => {
    if (data.length > 0) {
      dispatch(setTopDiscountCodesExportData({
        discountCodes: data,
        type
      }))
    }
  }, [data, type, dispatch])

  function toAverageDiscountPercentage(d: topDiscountData | topDiscountRulesData): string {
    // Average discount in percentage can be calculated with a fixed currency
    const total = Dinero({ amount: d.subTotal?.value })
    const discount = Dinero({ amount: d.discountTotal?.value })

    if (discount.isZero()) {
      return `0%`;
    }

    const discountPercentage = discount
      .multiply(100)
      .divide(total.getAmount())

    return `~${Math.round(discountPercentage.getAmount())}%`
  }

  const tableHeader = (
    <StyledTableHeader>
      <div>Rank</div>
      <div>Discount {type === 'codes' ? 'Code' : type === 'code rules' ? 'Code Rule' : 'External Rule'}</div>
      <div>Usage</div>
      <div className="hideOnSmallScreens">Top Market</div>
      <div className="hideOnSmallScreens">Top Store</div>
      <div>Average Order Discount</div>
    </StyledTableHeader>
  )

  const toTooltipContent = (filterCount: number, totalCount: number) => {
    const percentage = Math.round((filterCount / totalCount) * 100);
    if (percentage === 100) {
      return `${filterCount} usage (100%)`;
    }

    return `${filterCount} usage (~${percentage}%)`;
  };

  const getRows = (d?: topDiscountData | topDiscountRulesData, index?: number) =>
    d ? (
      <TableRow
        key={d.key}
        handleOnClick={() => {
          type === "codes" ? navigate(`/discounts/discount-codes`) : type === "code rules" ? navigate(`/discounts/discount-code-rules/${d?.key}`) : navigate(`/discounts/external-rules/${d?.key}`)
        }}
      >
        <FlexColumn>
          <p>#{(index ?? 0) + 1}</p>
        </FlexColumn>
        <FlexColumnOverflow>
          <Tooltip id="discount-name-tooltip" />
          <SmallInfo
            data-tooltip-id="discount-name-tooltip"
            data-tooltip-content={(type === 'code rules' || type === 'external rules') && 'name' in d && d.name ? d.name : d.key}
            data-tooltip-delay-hide={100}
          >
            <TruncatedText>{(type === 'code rules' || type === 'external rules') && 'name' in d && d.name ? d.name : d.key}</TruncatedText>
          </SmallInfo>
        </FlexColumnOverflow>
        <FlexColumn>
          <p>{d.doc_count.toLocaleString()}</p>
        </FlexColumn>
        <FlexColumn className="hideOnSmallScreens">
          <Tooltip id="tooltip" />
          {d.byCountry.buckets[0].doc_count && (
            <SmallInfo
              className={"hideOnSmallScreens"}
              data-tooltip-id="tooltip"
              data-tooltip-content={toTooltipContent(d.byCountry.buckets[0].doc_count, d.doc_count)}
              data-tooltip-delay-hide={100}
            >
              <FlagIcon countryCode={d.byCountry.buckets[0].key} />
            </SmallInfo>
          )}
        </FlexColumn>
        <FlexColumn className="hideOnSmallScreens">
          <Tooltip id="tooltip2" />
          {d.byStoreGroupId.buckets[0].doc_count && (
            <SmallInfo
              className={"hideOnSmallScreens"}
              data-tooltip-id="tooltip2"
              data-tooltip-content={toTooltipContent(d.byStoreGroupId.buckets[0].doc_count, d.doc_count)}
              data-tooltip-delay-hide={100}
            >
              <p>{d.byStoreGroupId.buckets[0].key}</p>
            </SmallInfo>
          )}
        </FlexColumn>
        <FlexColumn>
          <p>{toAverageDiscountPercentage(d)}</p>
        </FlexColumn>
      </TableRow>
    ) : (
      <></>
    )

  if (loading)
    return <TableBlurLoading numberOfRows={4} tableHeader={tableHeader} rows={getRows(undefined, 0)} />

  return (
    <Container>
      {error && (
        <ErrorMessage
          showRefreshButton
          message={
            <>
              Error loading Top Discount Code List. Try refreshing the page, or contact{" "}
              <a href="mailto:support@brinkcommerce.com">support</a>.
            </>
          }
        />
      )}
      {data && data?.length > 0 ? (
        <>
          {tableHeader}
          <div>{data.map((order, index) => getRows(order, index))}</div>
        </>
      ) : (
        <EmptyList>
          <CartIcon />
          <p> No discount {type === 'codes' ? 'codes' : type === 'code rules' ? 'code rules' : 'external rules'} found</p>
        </EmptyList>
      )}
    </Container>
  )
}
